import { useMemo } from 'react'
import { create } from 'zustand'
import { useQueryClient } from '@tanstack/react-query'
import { useTopics } from '@/hooks/useConfig'
import { useEffectiveClient, useEffectiveClientId } from '@/hooks/useAuth'
import {
  ringItems, ringQuestion, picksAnswer, hasPicks, type RingItem, type Picks,
} from '@/components/onboarding/questionBank'
import {
  useVoiceInterview, useSaveInterview, useBuildVoiceProfile,
  type VoiceInterview, type InterviewAnswer, type RingNudge, type RingState,
} from '@/components/onboarding/useVoiceSeeding'

/**
 * The voice ring (Brian's after/voice-ring.js, Oct 31 to Nov 3): what's left of
 * the voice profile after onboarding's starter five. It counts the core ten, a
 * take per topic in the mix and the quick picks; every count reads as what's
 * left ("n of 18 left"). Every answer goes to voice_interviews.answers with its
 * question id, like an onboarding answer, and the profile is rebuilt when the
 * ring grows by a few answers or fills.
 */

/** Rebuild the profile every this many new answers, and when the ring fills. */
export const REBUILD_EVERY = 3
/** The voice-profile function's floor. */
export const PROFILE_MIN_ANSWERS = 3

/** The ring sheet's open state, shared by Emma's ring, her window's card, the
 *  offer and Settings. */
export const useVoiceSheet = create<{ open: boolean; setOpen: (o: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}))
export const openVoiceSheet = () => useVoiceSheet.getState().setOpen(true)

export interface RingProgress {
  n: number
  total: number
  left: number
  pct: number
  full: boolean
}

const answeredText = (a: InterviewAnswer) => !a.skipped && !!a.a?.trim()

export function useVoiceRing() {
  const clientId = useEffectiveClientId()
  const client = useEffectiveClient()
  const qc = useQueryClient()
  const { data: iv, isLoading } = useVoiceInterview()
  const { data: topics } = useTopics()
  const save = useSaveInterview()
  const build = useBuildVoiceProfile()

  const topicsByShare = useMemo(() => (topics ?? [])
    .filter((t) => (t.share ?? 0) > 0)
    .sort((a, b) => (b.share ?? 0) - (a.share ?? 0))
    .map((t) => t.topic), [topics])

  const ring: RingState = iv?.ring ?? {}
  const items = useMemo(() => ringItems(topicsByShare, ring.skipped ?? []), [topicsByShare, ring.skipped])
  const answered = useMemo(() => new Set((iv?.answers ?? []).filter(answeredText).map((a) => a.id)), [iv?.answers])
  const done = (it: RingItem) => answered.has(it.id)
  const n = items.filter(done).length
  const total = items.length
  const progress: RingProgress = { n, total, left: total - n, pct: total ? n / total : 0, full: total > 0 && n >= total }
  const ready = !!clientId && !isLoading && topics !== undefined

  /** The row as it is right now (the cache, so quick taps never undo each other). */
  function current(): VoiceInterview | null {
    return qc.getQueryData<VoiceInterview | null>(['voice-interview', clientId, 'en']) ?? iv ?? null
  }

  function patch(p: Parameters<typeof save.mutate>[0], then?: () => void) {
    const cur = current()
    // An account that never started the interview gets a row that says setup is done.
    const extra = cur ? {} : { step: 'done' as const }
    save.mutate({ ...extra, ...p }, { onSuccess: then })
  }

  function setRing(next: Partial<RingState>, then?: () => void) {
    const cur = current()
    patch({ ring: { ...(cur?.ring ?? {}), ...next } }, then)
  }

  /** After an answer: rebuild the profile when the ring grew by a few or filled. */
  function maybeRebuild(answers: InterviewAnswer[]) {
    const have = answers.filter(answeredText)
    const cnt = items.filter((it) => have.some((a) => a.id === it.id)).length
    const built = current()?.ring?.built_n ?? 0
    const full = cnt >= total
    if (have.length < PROFILE_MIN_ANSWERS || build.isPending) return
    if (have.length - built >= REBUILD_EVERY || (full && have.length > built)) build.mutate({})
  }

  function answer(it: RingItem, text: string) {
    const cur = current()
    const entry: InterviewAnswer = { id: it.id, q: it.kind === 'picks' ? 'Quick picks' : ringQuestion(it), a: text.trim() }
    const answers = [...(cur?.answers ?? []).filter((a) => a.id !== it.id), entry]
    const skipped = (cur?.ring?.skipped ?? []).filter((x) => x !== it.id)
    patch({ answers, ring: { ...(cur?.ring ?? {}), skipped } }, () => maybeRebuild(answers))
  }

  function skip(it: RingItem) {
    const cur = current()
    const skipped = [...(cur?.ring?.skipped ?? []).filter((x) => x !== it.id), it.id]
    setRing({ skipped })
  }

  /** The picks are saved as they're tapped; Done turns them into the `picks` answer. */
  function setPicks(p: Picks) { setRing({ picks: p }) }
  function finishPicks(p: Picks) {
    const item = items.find((x) => x.kind === 'picks')
    if (!item) return
    if (!hasPicks(p)) { skip(item); return }
    const cur = current()
    const entry: InterviewAnswer = { id: 'picks', q: 'Quick picks', a: picksAnswer(p, topicsByShare) }
    const answers = [...(cur?.answers ?? []).filter((a) => a.id !== 'picks'), entry]
    const skipped = (cur?.ring?.skipped ?? []).filter((x) => x !== 'picks')
    patch({ answers, ring: { ...(cur?.ring ?? {}), picks: p, skipped } }, () => maybeRebuild(answers))
  }

  function setNudge(next: Partial<RingNudge>) {
    setRing({ nudge: { ...(current()?.ring?.nudge ?? {}), ...next } })
  }

  return {
    ready, items, progress, done, topicsByShare, ring,
    place: client?.city_state ?? null,
    onboardedAt: client?.onboarded_at ?? null,
    answer, skip, setPicks, finishPicks, setNudge,
    rebuilding: build.isPending,
  }
}
